import { normalizeFileHost } from '@Core/lib/tasks/utils/file';
import type {
  ImagePriorityGrant,
  ImagePriorityToken
} from '@Pages/pageRuntime/hooks/useFileImageDelivery';
import { createContext } from 'react';

export type PageImageRuntime = {
  /** Workspace hosts whose absolute `/files/{uuid}` URLs name this workspace's files. */
  fileHosts: ReadonlySet<string>;
  priority: ImagePriorityGrant;
  /** True when every managed image must load eagerly, as a printed document requires. */
  eagerLoading: boolean;
};

export const PageImageRuntimeContext = createContext<PageImageRuntime | null>(null);

export function createImagePriorityGrant(): ImagePriorityGrant {
  const listeners = new Set<() => void>();
  let holder: ImagePriorityToken | null = null;
  let holderArea = 0;

  function notify() {
    for (const listener of Array.from(listeners)) {
      listener();
    }
  }

  return {
    holder: () => holder,
    claim(token, visibleArea) {
      if (holder === token) {
        holderArea = Math.max(holderArea, visibleArea);
        return true;
      }

      if (holder !== null && visibleArea <= holderArea) {
        return false;
      }

      holder = token;
      holderArea = visibleArea;
      notify();

      return true;
    },
    release(token) {
      if (holder !== token) {
        return;
      }

      holder = null;
      holderArea = 0;
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);

      return () => {
        listeners.delete(listener);
      };
    }
  };
}

export function createPageImageRuntime({
  fileHosts = [],
  priority,
  eagerLoading = false
}: {
  fileHosts?: readonly string[];
  priority: ImagePriorityGrant;
  eagerLoading?: boolean;
}): PageImageRuntime {
  const hosts = new Set<string>();

  for (const host of fileHosts) {
    const normalized = normalizeFileHost(host);

    if (normalized !== '') {
      hosts.add(normalized);
    }
  }

  return {
    fileHosts: hosts,
    priority,
    eagerLoading
  };
}
